import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import styled from 'styled-components';
import {
  Card, CardTitle, CardSub, Muted, Pill, ReasonPill, StyledTable, HeadCell, BodyRow, BodyCell,
  fmtClock, fmtSeconds, roundLabel, questionLabel,
} from './proctoringShared';
import { buildProctoringSummary, summaryToText } from './proctoringSummary';
import { FONT_BODY, COLORS, TYPE, RADIUS, CONTROL_HEIGHT } from '../../styles/recruiterTheme';

const FONT = FONT_BODY;

// Level colours follow the palette already used on this page (amber = needs a human).
const TONE = {
  review: { bg: '#FFF8E1', fg: '#E65100' },
  look:   { bg: '#E8DEF8', fg: '#4F378B' },
  clear:  { bg: '#E6F4EA', fg: '#2D7D46' },
  nodata: { bg: '#F1F5F9', fg: '#475569' },
};

const pct = (x) => (x === null || x === undefined ? 'n/a' : `${Math.round(x * 100)}%`);
const reasonText = (r) => String(r || 'unknown').replace(/[_-]+/g, ' ');

const Headline = styled.div`
  font-family: ${FONT};
  font-size: ${TYPE.body}px;
  font-weight: 600;
  color: ${COLORS.primaryDeep};
  line-height: 1.6;
  margin: 0 0 16px;
`;

const Signals = styled.ul`
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Signal = styled.li`
  font-family: ${FONT};
  font-size: ${TYPE.small}px;
  font-weight: 500;
  color: ${COLORS.ink};
  display: flex;
  gap: 8px;
  align-items: flex-start;
  &::before {
    content: '';
    flex: none;
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: 50%;
    background: ${(p) => (p.$severity === 'review' ? '#E65100' : '#7C5CBF')};
  }
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const Tile = styled.div`
  background: ${COLORS.surfaceTint};
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.md}px;
  padding: 16px;
`;

const TileNum = styled.div`
  font-family: ${FONT_BODY};
  font-weight: 800;
  font-size: ${TYPE.section}px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.primaryDeep};
  line-height: 1.2;
`;

const TileLbl = styled.div`
  font-family: ${FONT};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.muted};
  margin-top: 4px;
`;

const TileSub = styled.div`
  font-family: ${FONT};
  font-size: ${TYPE.caption}px;
  color: ${COLORS.muted};
  margin-top: 2px;
`;

const Cols = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const BlockTitle = styled.div`
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.body}px;
  color: ${COLORS.primaryDeep};
  margin-bottom: 8px;
`;

const NextStep = styled.div`
  font-family: ${FONT};
  font-size: ${TYPE.small}px;
  color: ${COLORS.ink};
  background: ${(p) => p.$bg};
  border-radius: ${RADIUS.md}px;
  padding: 12px 16px;
  line-height: 1.55;
`;

const ProctoringSummaryCard = ({ candidate, group, olderAttempts = 0 }) => {
  // null | 'copied' | 'failed'
  const [copyState, setCopyState] = useState(null);
  const copyTimer = useRef(null);
  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const summary = useMemo(() => buildProctoringSummary({
    sessions: group ? group.sessions : [],
    violations: (candidate && candidate.proctoringViolations) || {},
    proctoring: (candidate && candidate.proctoring) || {},
    olderAttempts,
  }), [candidate, group, olderAttempts]);

  const tone = TONE[summary.level] || TONE.nodata;
  const hasData = summary.level !== 'nodata';
  const { totals } = summary;

  const copy = () => {
    const text = summaryToText(summary, candidate && candidate.candidatename);
    const show = (state) => {
      setCopyState(state);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopyState(null), 2500);
    };
    // The clipboard API needs a secure context (https or localhost); say so instead of failing silently.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => show('copied')).catch(() => show('failed'));
    } else {
      show('failed');
    }
  };

  return (
    <Card elevation={0} sx={{ mt: 3 }}>
      <CardTitle>
        <span>Summary at a glance</span>
        <Pill $bg={tone.bg} $fg={tone.fg}>{summary.levelLabel}</Pill>
      </CardTitle>
      <CardSub>A prompt for where to look first. It is not a verdict.</CardSub>

      <Headline>{summary.headline}</Headline>

      {summary.signals.length > 0 && (
        <Signals aria-label="Why this level">
          {summary.signals.map((s) => (
            <Signal key={s.text} $severity={s.severity}>{s.text}</Signal>
          ))}
        </Signals>
      )}

      {hasData && (
        <Tiles>
          <Tile>
            <TileNum>{fmtClock(totals.monitoredS)}</TileNum>
            <TileLbl>Monitored</TileLbl>
          </Tile>
          <Tile>
            <TileNum>{fmtSeconds(totals.flaggedS)}</TileNum>
            <TileLbl>With a flag open</TileLbl>
            <TileSub>{totals.flaggedShare === null ? 'share not available' : `${pct(totals.flaggedShare)} of monitored time`}</TileSub>
          </Tile>
          <Tile>
            <TileNum>{totals.episodes}</TileNum>
            <TileLbl>Episodes</TileLbl>
            <TileSub>
              {totals.longest ? `longest ${fmtSeconds(totals.longest.seconds)}` : 'none flagged'}
            </TileSub>
          </Tile>
          <Tile>
            <TileNum>{totals.warnings}</TileNum>
            <TileLbl>Warnings sent</TileLbl>
          </Tile>
        </Tiles>
      )}

      {hasData && (
        <Cols>
          <Box>
            <BlockTitle>By reason</BlockTitle>
            {summary.byReason.length === 0 ? (
              <Muted>No episodes.</Muted>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <StyledTable size="small">
                  <TableHead>
                    <TableRow>
                      <HeadCell $dense>Reason</HeadCell>
                      <HeadCell $dense align="right">Episodes</HeadCell>
                      <HeadCell $dense align="right">Time</HeadCell>
                      <HeadCell $dense align="right">Share</HeadCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.byReason.map((r) => (
                      <BodyRow key={r.reason}>
                        <BodyCell $dense><ReasonPill reason={r.reason} /></BodyCell>
                        <BodyCell $dense align="right">{r.episodes}</BodyCell>
                        <BodyCell $dense align="right">{fmtSeconds(r.seconds)}</BodyCell>
                        <BodyCell $dense align="right">{pct(r.share)}</BodyCell>
                      </BodyRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>
            )}
          </Box>

          <Box>
            <BlockTitle>By round</BlockTitle>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <StyledTable size="small">
                <TableHead>
                  <TableRow>
                    <HeadCell $dense>Round</HeadCell>
                    <HeadCell $dense align="right">Monitored</HeadCell>
                    <HeadCell $dense align="right">Flagged</HeadCell>
                    <HeadCell $dense align="right">Episodes</HeadCell>
                    <HeadCell $dense align="right">Warnings</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.byRound.map((r) => (
                    <BodyRow key={r.round}>
                      <BodyCell $dense>{roundLabel(r.round)}</BodyCell>
                      {r.hasSession ? (
                        <>
                          <BodyCell $dense align="right">{fmtClock(r.monitoredS)}</BodyCell>
                          <BodyCell $dense align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {fmtSeconds(r.flaggedS)}{r.share === null ? '' : ` (${pct(r.share)})`}
                          </BodyCell>
                          <BodyCell $dense align="right">{r.episodes}</BodyCell>
                          <BodyCell $dense align="right">{r.warnings}</BodyCell>
                        </>
                      ) : (
                        <BodyCell $dense colSpan={4} align="right"><Muted>No camera session</Muted></BodyCell>
                      )}
                    </BodyRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
            {summary.missingRounds.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Muted>
                  No session for {summary.missingRounds.map((r) => roundLabel(r)).join(', ')}.
                  The round was either skipped for this role or the camera was off.
                </Muted>
              </Box>
            )}
          </Box>
        </Cols>
      )}

      {summary.hotSpots.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <BlockTitle>Hot spots</BlockTitle>
          {summary.hotSpots.map((h) => (
            <Box
              key={`${h.round}-${h.question_index}`}
              sx={{ fontFamily: FONT, fontSize: TYPE.small, color: COLORS.ink, py: 0.5 }}
            >
              <strong>{[roundLabel(h.round), questionLabel(h.question_index)].filter(Boolean).join(' ')}</strong>
              {` · ${fmtSeconds(h.seconds)} across ${h.episodes} ${h.episodes === 1 ? 'episode' : 'episodes'}, mostly ${reasonText(h.topReason)}`}
            </Box>
          ))}
        </Box>
      )}

      <NextStep $bg={tone.bg}>
        <strong>Suggested next step.</strong> {summary.nextStep}
      </NextStep>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        <Muted>
          {olderAttempts > 0
            ? `Covers the latest attempt only. ${olderAttempts} earlier ${olderAttempts === 1 ? 'attempt is' : 'attempts are'} listed below.`
            : ' '}
        </Muted>
        <Button
          variant="outlined"
          onClick={copy}
          aria-live="polite"
          sx={{
            height: CONTROL_HEIGHT.md, textTransform: 'none', fontWeight: 600, px: 2,
            color: copyState === 'failed' ? COLORS.error : COLORS.primary,
            borderColor: COLORS.borderStrong, borderRadius: `${RADIUS.sm}px`,
          }}
        >
          {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy summary'}
        </Button>
      </Box>
    </Card>
  );
};

export default ProctoringSummaryCard;